import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import {
  ESdkEvents,
  EventParams,
  SubscriptionStatus,
  GetSubscriptionResponse,
} from '../../../../contracts';
import { sendFEEvent } from '../../../../eventUtil';
import { useAPI } from '../../../../services/api.service';
import { useCheckout } from '../../../../hooks/use-checkout';
import { ECheckoutStatus } from '../../../../constants/enums';
import { usePaymentState } from '../../../Checkout/hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { useCheckoutFlowType } from '../../../Checkout/hooks/use-checkout-flow-type';

const FETCH_INTERVAL_TIME = 2500;
const AXIOS_NETWORK_ERR_CODE = 'ERR_NETWORK';
const MINUTE_IN_MILLISECONDS = 60000;

export function useSubscriptionPolling(handleErrorCallback?: () => void) {
  const { API } = useAPI();
  const interval = useRef<NodeJS.Timeout>();
  const connectionLostTimestamp = useRef<number>(0);
  const { getEventParams } = useCheckout();
  const { featureFlags } = useAppConfig();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const { isPaymentLinksFlow } = useCheckoutFlowType();

  const startSubscriptionPolling = (
    subscriptionId: string,
    FEeventParams: Partial<EventParams> = {}
  ) => {
    connectionLostTimestamp.current = Date.now();
    if (interval.current) {
      clearInterval(interval.current);
    }
    setCheckoutStatus(ECheckoutStatus.PENDING);
    interval.current = setInterval(
      fetchSubscriptionData,
      FETCH_INTERVAL_TIME,
      subscriptionId,
      FEeventParams
    );
  };

  async function fetchSubscriptionData(
    subscriptionId: string,
    FEeventParams?: Partial<EventParams>
  ) {
    try {
      const subscriptionResponse = await API.getSubscription(subscriptionId);
      handleSubscriptionResponse(subscriptionResponse, FEeventParams);
    } catch (error: any) {
      if (error.code === AXIOS_NETWORK_ERR_CODE) {
        handleConnectionLost(FEeventParams);
      } else {
        clearInterval(interval.current);
        sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
          ...getEventParams(),
          ...FEeventParams,
          error: {
            isFatal: true,
          },
        });
        showBoundary(t('somethingWentWrong'));
      }
    }
  }

  function handleSubscriptionResponse(
    subscription: GetSubscriptionResponse,
    FEeventParams?: Partial<EventParams>
  ) {
    if (subscription?.status === SubscriptionStatus.ACTIVE) {
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      const nextCheckoutStatus =
        featureFlags?.checkout_disable_success_screen && !isPaymentLinksFlow
          ? ECheckoutStatus.SUCCESS_SHOW_PENDING
          : ECheckoutStatus.SUCCESS;
      setCheckoutStatus(nextCheckoutStatus);
      clearInterval(interval.current);
    }
    // } else if (order?.state === EChargeStates.FAILED) {
    //   sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
    //     ...getEventParams(),
    //     ...FEeventParams,
    //     reason: order?.publisherErrorMessage,
    //   });
    //   const defaultErrorMessage = t('yourPaymentWasDeclined');
    //   setErrorMessage(order?.publisherErrorMessage || defaultErrorMessage);
    //   setCheckoutStatus(ECheckoutStatus.ERROR);
    //   clearInterval(interval.current);
    // } else if (order.state === EPaymentStates.FAILED) {
    //   // case of payment intent failed in paypal
    //   const defaultErrorMessage = t('yourPaymentWasDeclined');
    //   sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
    //     ...getEventParams(),
    //     ...FEeventParams,
    //     reason: order?.publisherErrorMessage || defaultErrorMessage,
    //   });
    //   handleErrorCallback?.();
    //   setErrorMessage(order?.publisherErrorMessage || defaultErrorMessage);
    //   setIsPaymentLoading(false);
    //   setCheckoutStatus(ECheckoutStatus.INIT);
    //   clearInterval(interval.current);
    // }
  }

  function handleConnectionLost(FEeventParams?: Partial<EventParams>) {
    if (Date.now() - connectionLostTimestamp.current >= MINUTE_IN_MILLISECONDS) {
      clearInterval(interval.current);
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: 'Connection lost',
        error: {
          isFatal: true,
          reason: 'Connection lost',
        },
      });
      showBoundary(t('checkTheGameForYourItems'));
    }
  }

  return { startSubscriptionPolling };
}
